<!-- Similar structure but with loading states -->
<template>
  <div class="blog-home-container blog-sub-pages">
    <breadcrumbs :breadcrumbs="breadcrumbs" />

    <div class="blog-subpage-header">
      <div class="blog-subpage-header-content">
        <h1><span class="category-name s-badge s-badge-dark">{{ pageData.categorySlug }}</span><br class="s-show-mobile-l"> Category</h1>
        <div class="blog-meta">
          <span class="article-count">{{ pageData.totalArticles || 0 }} articles</span>
        </div>
      </div>
    </div>

    <div class="blog-layout">
      <div class="blog-main articles-parent blog-articles">
        <div v-if="pageData.error" class="error-message">
          {{ pageData.error }}
        </div>

        <div v-else-if="!pageData.articles?.length" class="no-articles">
          No articles found in this category
        </div>

        <div v-else class="article-grid">
          <article
            v-for="article in visibleArticles"
            :key="article.id"
            class="article-card"
          >
            <a :href="`/pulse/articles/${article.id}-${article.slug}`" class="article-image-link">
              <img
                :src="article.imageSizes['thumbnail']?.url ?? article.image"
                :alt="article.title"
                class="article-image"
                @error="handleImageError"
                loading="lazy"
              >
            </a>

            <div class="article-content">
              <div class="content-wrapper">
                <div class="article-meta">
                  <span class="date">{{ formatDate(article.date) }}</span>
                </div>

                <h2 class="article-title">
                  <a :href="`/pulse/articles/${article.id}-${article.slug}`">
                    {{ article.title }}
                  </a>
                </h2>

                <p class="article-description">{{ article.preview }}</p>
              </div>

              <div class="article-tags" v-if="article.tags?.length">
                <a
                  v-for="tag in article.tags"
                  :key="tag.slug"
                  :href="`/pulse/tags/${tag.slug}`"
                  class="tag s-badge s-badge-blog-tag"
                >
                  #{{ tag.name }}
                </a>
              </div>
            </div>
          </article>
        </div>

        <!-- Load More Section -->
        <div
          v-if="hasMoreArticles"
          ref="loadMoreTrigger"
          class="read-more"
        >
          <s-button
            class="button-primary-dark s-button-hover-rise"
            @click="loadMore"
            :loading="loading"
          >
            {{ loading ? 'Loading...' : 'Show More Articles' }}
          </s-button>
        </div>
      </div>

      <BlogSidePanel :other-categories="otherCategories"/>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watchEffect } from 'vue';
import { useData } from 'vike-vue/useData';
import SButton from '#root/components/SButton.vue';
import Breadcrumbs from "#root/components/Breadcrumbs.vue";
import BlogSidePanel from "#root/components/BlogSidePanel.vue";
import { getArticlesByCategory } from '/services/contentService';

// Get the data from Vike and keep it reactive
const pageData = useData();

// Filter out current category and memoize the result
const otherCategories = computed(() =>
  pageData.categories?.filter(cat => cat?.slug !== pageData.categorySlug) ?? []
);

// State with proper initialization
const currentPage = ref(1);
const itemsPerPage = 9;
const loadMoreTrigger = ref(null);
const observer = ref(null);
const loading = ref(false);
const autoLoadCount = ref(0);

// Computed properties with memoization for better performance
const visibleArticles = computed(() => {
  if (!pageData.articles) return [];
  const endIndex = currentPage.value * itemsPerPage;
  return pageData.articles.slice(0, endIndex);
});

const hasMoreArticles = computed(() => {
  if (!pageData.articles) return false;
  return currentPage.value * itemsPerPage < pageData.totalArticles;
});

const breadcrumbs = computed(() => {
  return [
    {
      name: 'Home',
      path: '/pulse'
    },
    {
      name: 'Explore Categories',
      path: '/pulse/categories'
    },
    {
      name: pageData.categorySlug
    }
  ];
});

// Methods
const loadMore = async (isAutoLoad = false) => {
  if (loading.value || !hasMoreArticles.value) return;

  // If it's an auto-load and we've reached the limit, don't proceed
  if (isAutoLoad && autoLoadCount.value >= 2) return;

  loading.value = true;
  try {
    const nextPage = currentPage.value + 1;
    const result = await getArticlesByCategory(pageData.categorySlug, 9, nextPage);
    const newArticles = result.categoryResults[pageData.categorySlug].articles;

    pageData.articles = [...pageData.articles, ...newArticles];
    currentPage.value = nextPage;

    // Increment auto-load counter if this was triggered automatically
    if (isAutoLoad) {
      autoLoadCount.value++;
    }
  } catch (error) {
    console.error('Error loading more articles:', error);
  } finally {
    loading.value = false;
  }
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const handleImageError = (event) => {
  event.target.src = 'https://cdn.stocksnap.io/img-thumbs/960w/architecture-building_YC9NVCLK9O.jpg';
};

// Optimized infinite scroll with cleanup
const setupInfiniteScroll = () => {
  if (observer.value) {
    observer.value.disconnect();
  }

  observer.value = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting && hasMoreArticles.value && !loading.value) {
        loadMore(true); // Pass true to indicate this is an auto-load
      }
    },
    { threshold: 0.1 }
  );

  if (loadMoreTrigger.value) {
    observer.value.observe(loadMoreTrigger.value);
  }
};

// Watch for changes in the loadMoreTrigger ref
watchEffect(() => {
  if (loadMoreTrigger.value) {
    setupInfiniteScroll();
  }
});

// Cleanup
onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect();
  }
});
</script>
